// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Blackjack
// Premium blackjack with provably fair card dealing
// ================================================================
import React, { useState, useCallback, useRef, useEffect } from 'react';
import ProvablyFairEngine from '../../engine/ProvablyFairEngine';
import { useCasino } from '../../context/CasinoShell';

// Full 52-card deck
const SUITS = ['♠', '♥', '♦', '♣'];
const SUIT_COLORS = { '♠': '#E8E8E8', '♥': '#EF4444', '♦': '#3B82F6', '♣': '#22C55E' };
const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

function buildDeck() {
  const deck = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank, value: rank === 'A' ? 11 : ['J','Q','K'].includes(rank) ? 10 : parseInt(rank) });
    }
  }
  return deck;
}

function handValue(hand) {
  let total = hand.reduce((s, c) => s + c.value, 0);
  let aces = hand.filter(c => c.rank === 'A').length;
  while (total > 21 && aces > 0) { total -= 10; aces--; }
  return total;
}

function isSoft(hand) {
  let total = hand.reduce((s, c) => s + c.value, 0);
  return total !== handValue(hand);
}

const BET_OPTIONS = [1, 5, 10, 25, 50, 100];

export default function Blackjack() {
  const { forgeBalance, placeBet, recordWin, recordLoss, logFairness, isExcluded } = useCasino();
  const engineRef = useRef(new ProvablyFairEngine());

  const [bet, setBet] = useState(5);
  const [playerHand, setPlayerHand] = useState([]);
  const [dealerHand, setDealerHand] = useState([]);
  const [deck, setDeck] = useState([]);
  const [gameState, setGameState] = useState('betting'); // betting, playing, dealerTurn, resolved
  const [result, setResult] = useState(null);
  const [lastWin, setLastWin] = useState(null);
  const [doubledDown, setDoubledDown] = useState(false);
  const [dealerRevealed, setDealerRevealed] = useState(false);
  const [commitmentHash, setCommitmentHash] = useState(null);
  const [dealAnim, setDealAnim] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    async function init() {
      const commitment = await engineRef.current.createCommitment();
      setCommitmentHash(commitment.commitmentHash);
    }
    init();
  }, []);

  // Seeded shuffle using provably fair hash
  const shuffleDeck = useCallback((hash) => {
    const d = buildDeck();
    // Fisher-Yates using hash bytes
    for (let i = d.length - 1; i > 0; i--) {
      const hexPos = ((d.length - 1 - i) * 4) % (hash.length - 4);
      const j = parseInt(hash.slice(hexPos, hexPos + 4), 16) % (i + 1);
      [d[i], d[j]] = [d[j], d[i]];
    }
    return d;
  }, []);

  const deal = useCallback(async () => {
    if (gameState !== 'betting') return;
    if (isExcluded()) return;

    const betResult = placeBet(bet, 'blackjack');
    if (!betResult.success) return;

    const clientSeed = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    const fairResult = await engineRef.current.generateResult(clientSeed);
    const proof = engineRef.current.getProof();

    const shuffled = shuffleDeck(fairResult.resultHash);
    setDeck(shuffled.slice(4));

    // Deal with animation
    setDealAnim([]);
    setDealerRevealed(false);
    setDoubledDown(false);
    setResult(null);
    setLastWin(null);

    const p = [shuffled[0], shuffled[2]];
    const d = [shuffled[1], shuffled[3]];

    // Animate dealing
    for (let i = 0; i < 4; i++) {
      await new Promise(r => setTimeout(r, 200));
      setDealAnim(prev => [...prev, i]);
    }

    setPlayerHand(p);
    setDealerHand(d);

    // Check for natural blackjack
    const pVal = handValue(p);
    const dVal = handValue(d);

    if (pVal === 21) {
      setDealerRevealed(true);
      if (dVal === 21) {
        finishGame(p, d, shuffled.slice(4), 'push', bet, proof);
      } else {
        finishGame(p, d, shuffled.slice(4), 'blackjack', bet, proof);
      }
    } else {
      setGameState('playing');
    }

    logFairness({ gameId: 'blackjack', ...proof, betAmount: bet });
  }, [gameState, bet, placeBet, isExcluded, shuffleDeck, logFairness]);

  const hit = useCallback(() => {
    if (gameState !== 'playing') return;
    const newCard = deck[0];
    const newDeck = deck.slice(1);
    const newHand = [...playerHand, newCard];
    setPlayerHand(newHand);
    setDeck(newDeck);

    if (handValue(newHand) > 21) {
      setDealerRevealed(true);
      finishGame(newHand, dealerHand, newDeck, 'bust', bet);
    }
  }, [gameState, deck, playerHand, dealerHand, bet]);

  const stand = useCallback(async () => {
    if (gameState !== 'playing') return;
    setGameState('dealerTurn');
    setDealerRevealed(true);

    let dHand = [...dealerHand];
    let dDeck = [...deck];

    // Dealer draws (stands on soft 17)
    while (handValue(dHand) < 17) {
      await new Promise(r => setTimeout(r, 500));
      dHand = [...dHand, dDeck[0]];
      dDeck = dDeck.slice(1);
      setDealerHand([...dHand]);
      setDeck([...dDeck]);
    }

    const pVal = handValue(playerHand);
    const dVal = handValue(dHand);

    if (dVal > 21) {
      finishGame(playerHand, dHand, dDeck, 'dealerBust', doubledDown ? bet * 2 : bet);
    } else if (pVal > dVal) {
      finishGame(playerHand, dHand, dDeck, 'win', doubledDown ? bet * 2 : bet);
    } else if (pVal < dVal) {
      finishGame(playerHand, dHand, dDeck, 'lose', doubledDown ? bet * 2 : bet);
    } else {
      finishGame(playerHand, dHand, dDeck, 'push', doubledDown ? bet * 2 : bet);
    }
  }, [gameState, dealerHand, deck, playerHand, bet, doubledDown]);

  const doubleDown = useCallback(() => {
    if (gameState !== 'playing' || playerHand.length !== 2) return;
    if (forgeBalance < bet) return;

    const ddResult = placeBet(bet, 'blackjack');
    if (!ddResult.success) return;

    setDoubledDown(true);
    const newCard = deck[0];
    const newDeck = deck.slice(1);
    const newHand = [...playerHand, newCard];
    setPlayerHand(newHand);
    setDeck(newDeck);

    if (handValue(newHand) > 21) {
      setDealerRevealed(true);
      finishGame(newHand, dealerHand, newDeck, 'bust', bet * 2);
    } else {
      // Automatically stand after double down
      setTimeout(() => {
        setGameState('playing');
        // Trigger stand
      }, 300);
      // Stand logic inline
      setGameState('dealerTurn');
      setDealerRevealed(true);
      let dHand = [...dealerHand];
      let dDeck = [...newDeck];

      const runDealer = async () => {
        while (handValue(dHand) < 17) {
          await new Promise(r => setTimeout(r, 500));
          dHand = [...dHand, dDeck[0]];
          dDeck = dDeck.slice(1);
          setDealerHand([...dHand]);
          setDeck([...dDeck]);
        }
        const pVal = handValue(newHand);
        const dVal = handValue(dHand);
        if (dVal > 21) finishGame(newHand, dHand, dDeck, 'dealerBust', bet * 2);
        else if (pVal > dVal) finishGame(newHand, dHand, dDeck, 'win', bet * 2);
        else if (pVal < dVal) finishGame(newHand, dHand, dDeck, 'lose', bet * 2);
        else finishGame(newHand, dHand, dDeck, 'push', bet * 2);
      };
      runDealer();
    }
  }, [gameState, playerHand, deck, dealerHand, bet, forgeBalance, placeBet]);

  const finishGame = (pHand, dHand, remainingDeck, outcome, totalBet, proof) => {
    setGameState('resolved');
    setDealerRevealed(true);
    const pVal = handValue(pHand);
    const dVal = handValue(dHand);

    let winAmount = 0;
    let resultText = '';

    switch (outcome) {
      case 'blackjack':
        winAmount = totalBet * 2.5; // 3:2 payout
        resultText = 'BLACKJACK!';
        break;
      case 'win':
      case 'dealerBust':
        winAmount = totalBet * 2;
        resultText = outcome === 'dealerBust' ? 'DEALER BUSTS!' : 'YOU WIN!';
        break;
      case 'push':
        winAmount = totalBet;
        resultText = 'PUSH';
        break;
      case 'bust':
        winAmount = 0;
        resultText = 'BUST!';
        break;
      case 'lose':
        winAmount = 0;
        resultText = 'DEALER WINS';
        break;
      default: break;
    }

    if (winAmount > 0) {
      recordWin(winAmount, 'blackjack', proof?.resultHash);
      setLastWin(winAmount - totalBet);
    }
    if (winAmount === 0) {
      recordLoss(totalBet, 'blackjack', proof?.resultHash);
    }

    setResult({ outcome, resultText, winAmount, totalBet, pVal, dVal });
    setHistory(prev => [{
      outcome, pVal, dVal,
      bet: totalBet,
      win: winAmount > totalBet ? winAmount - totalBet : 0,
      time: new Date().toLocaleTimeString(),
    }, ...prev].slice(0, 15));
  };

  const newRound = useCallback(async () => {
    setGameState('betting');
    setPlayerHand([]);
    setDealerHand([]);
    setDealAnim([]);
    setResult(null);
    setDealerRevealed(false);
    const commitment = await engineRef.current.createCommitment();
    setCommitmentHash(commitment.commitmentHash);
  }, []);

  const renderCard = (card, index, hidden = false, animDelay = 0) => {
    const isRed = card.suit === '♥' || card.suit === '♦';
    return (
      <div key={index} style={{
        ...s.card,
        ...(hidden ? s.cardHidden : {}),
        animation: `cardDeal 0.3s ease-out ${animDelay}s both`,
        transform: hidden ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}>
        {hidden ? (
          <div style={s.cardBack}>
            <div style={s.cardBackPattern} />
          </div>
        ) : (
          <div style={s.cardFront}>
            <span style={{ ...s.cardRank, color: isRed ? '#EF4444' : '#E8E8E8' }}>{card.rank}</span>
            <span style={{ ...s.cardSuit, color: SUIT_COLORS[card.suit] }}>{card.suit}</span>
          </div>
        )}
      </div>
    );
  };

  const pVal = handValue(playerHand);
  const dVal = dealerRevealed ? handValue(dealerHand) : (dealerHand[0] ? dealerHand[0].value : 0);

  return (
    <div style={s.container}>
      <style>{`
        @keyframes cardDeal { from { opacity:0; transform:translateY(-40px) scale(0.8); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes winPulse { 0%,100%{text-shadow:0 0 20px #FFD700,0 0 40px #FFD700;} 50%{text-shadow:0 0 10px #FFD700,0 0 20px #FFD70066;} }
        @keyframes chipStack { from{transform:translateY(-20px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes feltShine { 0%{background-position:200% 50%} 100%{background-position:-200% 50%} }
      `}</style>

      {/* Premium felt background header */}
      <div style={s.header}>
        <div style={s.headerGlow} />
        <h2 style={s.title}>BLACKJACK</h2>
        <div style={s.headerStats}>
          <div style={s.statChip}>
            <span style={s.statLabel}>BALANCE</span>
            <span style={s.statVal}>${forgeBalance.toFixed(2)}</span>
          </div>
          {lastWin !== null && lastWin > 0 && (
            <div style={{ ...s.statChip, borderColor: '#FFD700' }}>
              <span style={{ ...s.statLabel, color: '#FFD700' }}>LAST WIN</span>
              <span style={{ ...s.statVal, color: '#FFD700' }}>+${lastWin.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Commitment hash */}
      <div style={s.commitBar}>
        <span style={s.commitLabel}>FAIR HASH:</span>
        <span style={s.commitHash}>{commitmentHash ? commitmentHash.slice(0, 16) + '...' : '...'}</span>
      </div>

      {/* Game Table - Premium Felt */}
      <div style={s.table}>
        <div style={s.feltOverlay} />

        {/* Dealer section */}
        <div style={s.dealerArea}>
          <div style={s.areaLabel}>
            <span style={s.labelText}>DEALER</span>
            {dealerHand.length > 0 && (
              <span style={s.handVal}>{dealerRevealed ? handValue(dealerHand) : dealerHand[0]?.value}</span>
            )}
          </div>
          <div style={s.handArea}>
            {dealerHand.map((card, i) =>
              renderCard(card, i, i === 1 && !dealerRevealed, i * 0.15)
            )}
          </div>
        </div>

        {/* Divider with result */}
        <div style={s.divider}>
          {result && (
            <div style={{
              ...s.resultBanner,
              background: result.outcome === 'bust' || result.outcome === 'lose'
                ? 'linear-gradient(135deg, #7F1D1D 0%, #991B1B 100%)'
                : result.outcome === 'push'
                  ? 'linear-gradient(135deg, #1E293B 0%, #334155 100%)'
                  : 'linear-gradient(135deg, #14532D 0%, #166534 100%)',
              borderColor: result.outcome === 'bust' || result.outcome === 'lose'
                ? '#DC2626' : result.outcome === 'push' ? '#64748B' : '#22C55E',
            }}>
              <span style={s.resultText}>{result.resultText}</span>
              {result.winAmount > result.totalBet && (
                <span style={s.resultWin}>+${(result.winAmount - result.totalBet).toFixed(2)}</span>
              )}
            </div>
          )}
          {!result && gameState !== 'betting' && (
            <div style={s.vsLine} />
          )}
        </div>

        {/* Player section */}
        <div style={s.playerArea}>
          <div style={s.areaLabel}>
            <span style={s.labelText}>YOU</span>
            {playerHand.length > 0 && (
              <span style={{
                ...s.handVal,
                color: pVal > 21 ? '#EF4444' : pVal === 21 ? '#FFD700' : '#fff',
              }}>{pVal}</span>
            )}
          </div>
          <div style={s.handArea}>
            {playerHand.map((card, i) => renderCard(card, i, false, i * 0.15))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={s.controls}>
        {gameState === 'betting' && (
          <>
            <div style={s.betSection}>
              <span style={s.betLabel}>WAGER</span>
              <div style={s.betChips}>
                {BET_OPTIONS.map(b => (
                  <button
                    key={b}
                    style={{
                      ...s.chip,
                      background: bet === b
                        ? 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
                        : 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                      borderColor: bet === b ? '#EF4444' : '#334155',
                      boxShadow: bet === b ? '0 0 12px #DC262666, 0 2px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.3)',
                    }}
                    onClick={() => setBet(b)}
                  >
                    <span style={s.chipVal}>${b}</span>
                  </button>
                ))}
              </div>
            </div>
            <button
              style={{
                ...s.dealBtn,
                opacity: forgeBalance < bet ? 0.4 : 1,
              }}
              onClick={deal}
              disabled={forgeBalance < bet}
            >
              <span style={s.dealBtnText}>DEAL</span>
              <span style={s.dealBtnSub}>${bet.toFixed(2)}</span>
            </button>
          </>
        )}

        {gameState === 'playing' && (
          <div style={s.actionBtns}>
            <button style={s.actionBtn} onClick={hit}>
              <span style={s.actionIcon}>{'👆'}</span>
              <span>HIT</span>
            </button>
            <button style={{ ...s.actionBtn, ...s.standBtn }} onClick={stand}>
              <span style={s.actionIcon}>{'✋'}</span>
              <span>STAND</span>
            </button>
            {playerHand.length === 2 && forgeBalance >= bet && (
              <button style={{ ...s.actionBtn, ...s.doubleBtn }} onClick={doubleDown}>
                <span style={s.actionIcon}>{'⬇️'}</span>
                <span>DOUBLE</span>
              </button>
            )}
          </div>
        )}

        {gameState === 'dealerTurn' && (
          <div style={s.waitMsg}>Dealer drawing...</div>
        )}

        {gameState === 'resolved' && (
          <button style={s.newRoundBtn} onClick={newRound}>
            NEW HAND
          </button>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div style={s.historySection}>
          <span style={s.historyTitle}>HAND HISTORY</span>
          <div style={s.historyScroll}>
            {history.slice(0, 8).map((h, i) => (
              <div key={i} style={s.historyItem}>
                <span style={{
                  ...s.historyDot,
                  background: h.outcome === 'win' || h.outcome === 'dealerBust' || h.outcome === 'blackjack'
                    ? '#22C55E' : h.outcome === 'push' ? '#64748B' : '#EF4444',
                }} />
                <span style={s.historyText}>
                  {h.pVal} vs {h.dVal}
                </span>
                <span style={{
                  ...s.historyAmount,
                  color: h.win > 0 ? '#22C55E' : h.outcome === 'push' ? '#64748B' : '#EF4444',
                }}>
                  {h.win > 0 ? `+$${h.win.toFixed(2)}` : h.outcome === 'push' ? 'Push' : `-$${h.bet.toFixed(2)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer info */}
      <div style={s.footer}>
        <span>Dealer stands on 17 • Blackjack pays 3:2 • Provably Fair SHA-256</span>
      </div>
    </div>
  );
}

const s = {
  container: {
    padding: 0,
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    position: 'relative',
    padding: '16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderBottom: '1px solid #334155',
    overflow: 'hidden',
  },
  headerGlow: {
    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
    width: 200, height: 60,
    background: 'radial-gradient(ellipse, #DC262620 0%, transparent 70%)',
  },
  title: {
    fontSize: 16, fontWeight: 800, color: '#F8FAFC', letterSpacing: 4,
    textAlign: 'center', margin: 0,
    textShadow: '0 0 20px #DC262644',
  },
  headerStats: {
    display: 'flex', justifyContent: 'center', gap: 12, marginTop: 10,
  },
  statChip: {
    padding: '4px 12px', borderRadius: 20,
    background: 'rgba(15,23,42,0.8)', border: '1px solid #334155',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  statLabel: { fontSize: 7, color: '#64748B', fontWeight: 700, letterSpacing: 1 },
  statVal: { fontSize: 14, fontWeight: 800, color: '#F8FAFC', fontFamily: "'SF Mono', monospace" },
  commitBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '4px 12px', background: '#0F172A', borderBottom: '1px solid #1E293B',
  },
  commitLabel: { fontSize: 7, color: '#475569', fontWeight: 700, letterSpacing: 1 },
  commitHash: { fontSize: 8, color: '#22C55E', fontFamily: 'monospace' },
  table: {
    position: 'relative',
    background: 'linear-gradient(180deg, #14532D 0%, #166534 30%, #15803D 50%, #166534 70%, #14532D 100%)',
    padding: '20px 12px',
    minHeight: 280,
    borderLeft: '4px solid #854D0E',
    borderRight: '4px solid #854D0E',
    overflow: 'hidden',
  },
  feltOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.3) 100%)',
    pointerEvents: 'none',
  },
  dealerArea: {
    position: 'relative', zIndex: 1,
    marginBottom: 12,
  },
  playerArea: {
    position: 'relative', zIndex: 1,
    marginTop: 12,
  },
  areaLabel: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
  },
  labelText: {
    fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.5)',
    letterSpacing: 2, textTransform: 'uppercase',
  },
  handVal: {
    fontSize: 18, fontWeight: 900, color: '#fff',
    textShadow: '0 0 8px rgba(0,0,0,0.5)',
    fontFamily: "'SF Mono', monospace",
  },
  handArea: {
    display: 'flex', gap: 6, minHeight: 90, alignItems: 'center',
    paddingLeft: 8,
  },
  card: {
    width: 60, height: 88,
    borderRadius: 8,
    perspective: 1000,
    flexShrink: 0,
  },
  cardFront: {
    width: '100%', height: '100%',
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F1F5F9 100%)',
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3), 0 1px 3px rgba(0,0,0,0.2)',
  },
  cardBack: {
    width: '100%', height: '100%',
    background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 50%, #2563EB 100%)',
    borderRadius: 8,
    border: '2px solid #3B82F6',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  cardBackPattern: {
    width: 36, height: 52, borderRadius: 4,
    border: '2px solid rgba(255,255,255,0.15)',
    background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.05) 3px, rgba(255,255,255,0.05) 6px)',
  },
  cardHidden: {},
  cardRank: {
    fontSize: 22, fontWeight: 900,
    fontFamily: "'Georgia', serif",
    lineHeight: 1,
  },
  cardSuit: {
    fontSize: 20, marginTop: -2,
  },
  divider: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '8px 0', position: 'relative', zIndex: 2,
  },
  vsLine: {
    width: '60%', height: 1,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
  },
  resultBanner: {
    padding: '8px 24px', borderRadius: 20,
    border: '2px solid',
    display: 'flex', alignItems: 'center', gap: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
  },
  resultText: {
    fontSize: 14, fontWeight: 900, color: '#fff',
    letterSpacing: 2, textShadow: '0 1px 4px rgba(0,0,0,0.5)',
  },
  resultWin: {
    fontSize: 14, fontWeight: 900, color: '#FFD700',
    animation: 'winPulse 1.5s ease-in-out infinite',
  },
  controls: {
    padding: '16px',
    background: 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)',
    borderTop: '3px solid #854D0E',
  },
  betSection: { textAlign: 'center', marginBottom: 12 },
  betLabel: { fontSize: 9, color: '#64748B', fontWeight: 700, letterSpacing: 2, display: 'block', marginBottom: 8 },
  betChips: {
    display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap',
  },
  chip: {
    width: 48, height: 48, borderRadius: '50%',
    border: '2px solid',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.15s ease',
  },
  chipVal: { fontSize: 10, fontWeight: 800, color: '#F8FAFC' },
  dealBtn: {
    width: '100%', padding: '14px',
    borderRadius: 12, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)',
    cursor: 'pointer',
    boxShadow: '0 4px 16px #DC262644, 0 2px 4px rgba(0,0,0,0.3)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  dealBtnText: {
    fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: 4,
  },
  dealBtnSub: {
    fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 600,
  },
  actionBtns: {
    display: 'flex', gap: 8,
  },
  actionBtn: {
    flex: 1, padding: '14px 8px',
    borderRadius: 12, border: '2px solid #DC2626',
    background: 'linear-gradient(180deg, #DC2626 0%, #B91C1C 100%)',
    color: '#fff', fontSize: 12, fontWeight: 900,
    letterSpacing: 1, cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    boxShadow: '0 4px 12px #DC262644',
  },
  standBtn: {
    background: 'linear-gradient(180deg, #1E40AF 0%, #1E3A8A 100%)',
    borderColor: '#3B82F6',
    boxShadow: '0 4px 12px #3B82F644',
  },
  doubleBtn: {
    background: 'linear-gradient(180deg, #CA8A04 0%, #A16207 100%)',
    borderColor: '#EAB308',
    boxShadow: '0 4px 12px #EAB30844',
  },
  actionIcon: { fontSize: 18 },
  waitMsg: {
    textAlign: 'center', fontSize: 14, fontWeight: 700,
    color: '#64748B', letterSpacing: 2, padding: 12,
  },
  newRoundBtn: {
    width: '100%', padding: '14px',
    borderRadius: 12, border: '2px solid #22C55E',
    background: 'linear-gradient(180deg, #16A34A 0%, #15803D 100%)',
    color: '#fff', fontSize: 14, fontWeight: 900,
    letterSpacing: 3, cursor: 'pointer',
    boxShadow: '0 4px 16px #22C55E44',
  },
  historySection: {
    padding: '12px 16px',
    background: '#0F172A',
    borderTop: '1px solid #1E293B',
  },
  historyTitle: {
    fontSize: 8, color: '#475569', fontWeight: 700, letterSpacing: 2,
    display: 'block', marginBottom: 8,
  },
  historyScroll: { display: 'flex', flexDirection: 'column', gap: 4 },
  historyItem: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '4px 8px', borderRadius: 6,
    background: 'rgba(30,41,59,0.5)',
  },
  historyDot: {
    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
  },
  historyText: { fontSize: 10, color: '#94A3B8', flex: 1, fontFamily: 'monospace' },
  historyAmount: { fontSize: 10, fontWeight: 700, fontFamily: 'monospace' },
  footer: {
    padding: '12px 16px', textAlign: 'center',
    fontSize: 8, color: '#475569', letterSpacing: 1,
    background: '#0F172A',
  },
};
